import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, UserCheck, BookOpen, FileSpreadsheet, TrendingUp, GraduationCap, AlertCircle, UserX } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

function Stat({ icon: Icon, label, value, hint, tone = "navy" }) {
  const tones = {
    navy: "bg-[hsl(var(--brand-navy))]/10 text-[hsl(var(--brand-navy))]",
    orange: "bg-[hsl(var(--brand-orange))]/15 text-[hsl(var(--brand-orange))]",
    green: "bg-emerald-500/15 text-emerald-700",
    rose: "bg-rose-500/15 text-rose-700",
    violet: "bg-violet-500/15 text-violet-700",
  };
  return (
    <Card className="border-border/70">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-1 font-display text-2xl font-bold">{value}</div>
            {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OverviewTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);
  if (!d) return (
    <div className="grid gap-3 sm:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
  );
  const { users, attempts, content, daily_attempts, subject_performance, leaderboard } = d;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Total users" value={users.total} hint={`+${users.new_7d} in last 7d`} />
        <Stat icon={UserCheck} label="Students" value={users.students} hint={`${users.teachers} teachers`} tone="green" />
        <Stat icon={BookOpen} label="Questions" value={content.questions} hint={`${content.subjects} subjects`} tone="orange" />
        <Stat icon={FileSpreadsheet} label="Attempts" value={attempts.total} hint={`${attempts.in_progress} in progress`} tone="violet" />
        <Stat icon={GraduationCap} label="Courses" value={content.courses} tone="orange" />
        <Stat icon={TrendingUp} label="Attempts (30d)" value={attempts.last_30d} tone="green" />
        <Stat icon={AlertCircle} label="Open reports" value={content.open_reports} tone="rose" />
        <Stat icon={UserX} label="Suspended" value={users.suspended} hint={`${users.new_30d} new users / 30d`} tone="rose" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/70">
          <CardContent className="p-5">
            <div className="mb-2 font-display text-base font-bold">Attempts — last 14 days</div>
            {daily_attempts.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No attempts in the last 14 days.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={daily_attempts}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="attempts" stroke="hsl(219 65% 23%)" strokeWidth={2} name="Attempts" />
                  <Line type="monotone" dataKey="avg_pct" stroke="hsl(28 90% 54%)" strokeWidth={2} name="Avg %" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/70">
          <CardContent className="p-5">
            <div className="mb-2 font-display text-base font-bold">Subject-wise accuracy</div>
            {subject_performance.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Not enough data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={subject_performance}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                  <XAxis dataKey="subject" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="accuracy" fill="hsl(28 90% 54%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/70">
        <CardContent className="p-5">
          <div className="mb-3 font-display text-base font-bold">Top performers</div>
          {leaderboard.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No submitted attempts yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {leaderboard.map((row, i) => (
                <div key={row.user.id || i} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--brand-navy))] text-xs font-bold text-white">{i + 1}</div>
                    <div>
                      <div className="text-sm font-semibold">{row.user.name}</div>
                      <div className="text-xs text-muted-foreground">{row.user.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold">{row.best}%</div>
                    <div className="text-xs text-muted-foreground">{row.attempts} attempts</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
